import React, { useState, useEffect } from 'react'
import { t } from 'i18next'
import { Tab, type TabItem } from '@awell-health/design-system'
import { ErrorBoundary } from '@awell/ui-kit/compositions/ErrorPage'
import { Activity, type EmrReportMetadataField } from '../types'
import { ContextTab } from '../ContextTab'
import { UpdatesTab } from '../UpdatesTab'
import { EmrReportTab } from './tabs/EmrReportTab'
import { useEmrDetail } from '../../../hooks/useEmrDetail'
import { EmrMetadataTabContent } from './tabs/EmrMetadataTabContent'
import { ActivityDetailLoading } from '../ActivityDetailLoading'

interface Props {
  activity: Activity
}

export const EmrActivityDetails: React.FC<Props> = ({ activity }) => {
  const { report, loading } = useEmrDetail(activity.object.id)

  const [activeTab, setActiveTab] = useState<string>('report')
  const [metadata, setMetadata] = useState<Array<EmrReportMetadataField> | undefined>(undefined)

  useEffect(() => {
    setActiveTab('report')
  }, [activity.id])

  const handleEmrReport = (metadata?: Array<EmrReportMetadataField>): void => {
    setMetadata(metadata)
  }

  const tabs: TabItem[] = [
    {
      id: 'report',
      label: 'Message',
      onClick: () => setActiveTab('report')
    },
    {
      id: 'metadata',
      label: 'Metadata',
      onClick: () => setActiveTab('metadata')
    },
    {
      id: 'details',
      label: 'Details',
      onClick: () => setActiveTab('details')
    },
    {
      id: 'updates',
      label: t('tab_title_updates', { count: activity.sub_activities.length }),
      onClick: () => setActiveTab('updates')
    }
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 'report':
        return <EmrReportTab report={report} onLoadEMRReport={handleEmrReport} />
      case 'metadata':
        return <EmrMetadataTabContent metadata={metadata} />
      case 'details':
        return <ContextTab activity={activity} />
      case 'updates':
        return <UpdatesTab activity={activity} />
    }
  }

  return (
    <ErrorBoundary panel showStack={true}>
      <div id="emr-activity-details">
        <div className="sticky top-0 bg-white z-10">
          <Tab variant="bordered" items={tabs} selected={activeTab} fullWidth={true} id="emr-activity-details-tab" />
        </div>
        <div className="p-4" aria-label={activeTab} role="tabpanel" id={`emr-activity-details-${activeTab}`}>
          {loading ? <ActivityDetailLoading /> : renderTabContent()}
        </div>
      </div>
    </ErrorBoundary>
  )
}
